import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, User, Mail, Phone, Lock } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import Loader from "@/components/ui/loader";
import { useI18n } from '@/hooks/useI18n';

const RegisterFormContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: ''
  });
  
  const { signUp } = useAuth();
  const { t } = useI18n();

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!registerData.firstName.trim()) errors.push(t('register.firstNameRequired'));
    if (!registerData.lastName.trim()) errors.push(t('register.lastNameRequired'));
    if (!registerData.email.trim()) errors.push(t('register.emailRequired'));
    if (!registerData.phone.trim()) errors.push(t('register.phoneRequired'));
    if (!registerData.password || registerData.password.length < 6) {
      errors.push(t('register.passwordTooShort'));
    }


    if (errors.length > 0) {
      toast({
        title: t('register.validationErrorTitle'),
        description: errors.join(', '),
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const metadata = {
        first_name: registerData.firstName,
        last_name: registerData.lastName,
        phone: registerData.phone,
        role: 'travel_designer'
      };
      
      const { error } = await signUp(registerData.email, registerData.password, metadata);
      
      if (error) {
        let errorMessage = t('register.registrationFailed');
        
        if (error.message.includes("User already registered")) {
          errorMessage = t('register.accountExists');
        } else if (error.message.includes("Password should be at least")) {
          errorMessage = t('register.passwordTooWeak');
        } else if (error.message.includes("Invalid email")) {
          errorMessage = t('register.invalidEmail');
        }
        
        toast({
          title: t('register.registrationFailed'),
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: t('register.registrationCompletedTitle'),
          description: t('register.registrationCompletedDesc'),
        });
        setRegisterData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: ''
        });
      }
    } catch (err) {
      toast({
        title: t('register.registrationErrorTitle'),
        description: t('register.registrationErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">{t('profile.firstName')}</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              id="firstName" 
              value={registerData.firstName}
              onChange={(e) => setRegisterData({...registerData, firstName: e.target.value})}
              className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
              placeholder={t('register.placeholderFirstName')}
              required 
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">{t('profile.lastName')}</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              id="lastName" 
              value={registerData.lastName}
              onChange={(e) => setRegisterData({...registerData, lastName: e.target.value})}
              className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
              placeholder={t('register.placeholderLastName')}
              required 
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="registerEmail" className="text-sm font-medium text-gray-700">{t('auth.email')}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="registerEmail" 
            type="email" 
            value={registerData.email}
            onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
            className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder={t('register.placeholderEmail')}
            required 
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-sm font-medium text-gray-700">{t('profile.phone')}</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="phone" 
            type="tel" 
            value={registerData.phone}
            onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
            className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder={t('register.placeholderPhone')}
            required 
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="registerPassword" className="text-sm font-medium text-gray-700">{t('auth.password')}</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="registerPassword" 
            type="password" 
            value={registerData.password}
            onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
            className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder={t('register.placeholderPassword')}
            required 
            disabled={isLoading}
            minLength={6}
          />
        </div>
      </div>

      
      <Button 
        type="submit" 
        className="w-full bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200"
        disabled={isLoading}
      >
            {isLoading ? (
          <>
            <Loader size="sm" className="mr-3" />
            {t('register.registering')}
          </>
        ) : (
          <>
            <UserPlus className="w-5 h-5 mr-2" />
            {t('register.register')}
          </>
        )}
      </Button>
    </form>
  );
};

export default RegisterFormContent; 